/**
 * What may FOLLOW a spoken music command (#4278): courtesies, then at most a new clause.
 *
 * One list, because two readers have to agree about it. The music control classifier
 * (signaling) uses it to tell "pause the music for a second, please" from "pause the
 * music stand". The speech service uses it to tell "Nova, stop the music right now" —
 * a request about the music — from "Nova, stop the music is too loud", which is someone
 * who wants the companion to stop. When the two lists were kept by hand in two places
 * they disagreed within a day.
 *
 * PARSED, not matched. As one regex, "(a bit|a little bit|...)*" reaching for "$" has
 * several ways to read every "a bit", and 26 of them took 1.7 s on a line anyone can
 * paste into chat. "before" and "after" are deliberately not clause openers: "stop the
 * music after this song" is a request about LATER, not a pause now.
 */
const AMOUNTS = ['a little bit', 'a little', 'a touch', 'a smidge', 'a bit', 'a tad', 'slightly'];
const COURTESIES = [...AMOUNTS.flatMap(amount => [`${amount} more`, amount]), 'some more', 'more',
  'thank you', 'right now', 'real quick', 'for everyone', 'for a moment', 'for a minute', 'for a second', 'for a while', 'for a sec',
  'for a bit', 'for me', 'for us', 'for now', 'again', 'back', 'please', 'thanks', 'now', 'up']
  .sort((a, b) => b.length - a.length); // longest first: "a little bit" must not be read as "a little" and a stranded "bit"
const SMALL_AMOUNTS = new Set(AMOUNTS.flatMap(amount => [`${amount} more`, amount]));
const NEW_CLAUSE = /^(?:and|so|then|while|because)\b\s*(.*)$/;
export const MUSIC_MAX_COURTESIES = 6;

export interface MusicCommandTail {
  /** What a new clause went on to say; '' when nothing did. */
  more: string;
  /** An AMOUNT courtesy was used ("a little", "a bit more"): the level moves by the small step. "for a bit" is a duration and is not one. */
  small: boolean;
  /**
   * The words this reader treated as courtesy and threw away. Reported because a caller may need to look at them:
   * after a WHOLE command they are decoration, but after a single word one of them can be the command itself, and
   * only the reader knows where its own swallowing stopped (#4292 review — "music skip a bit back" skipped forward).
   */
  swallowed: string;
}

/**
 * @param tail the words after the command, lower-case and unpunctuated.
 * @returns null when those words make it not a command.
 */
export function readMusicTail(tail: string): MusicCommandTail | null {
  const started = tail.trim().replace(/\bjust\s+/g, '');
  let rest = started;
  let small = false;
  for (let i = 0; i < MUSIC_MAX_COURTESIES && rest; i++) {
    const courtesy = COURTESIES.find(phrase => rest === phrase || rest.startsWith(`${phrase} `));
    if (!courtesy) break;
    small ||= SMALL_AMOUNTS.has(courtesy);
    rest = rest.slice(courtesy.length).trim();
  }
  const swallowed = started.slice(0, started.length - rest.length).trim();
  if (!rest) return { more: '', small, swallowed };
  const clause = NEW_CLAUSE.exec(rest);
  return clause ? { more: clause[1].trim(), small, swallowed } : null;
}

/** Trailing courtesies of a spoken NAME, longest reading first: "chill now please" → ["chill now please", "chill now", "chill"]. */
export function withoutTrailingCourtesies(name: string): string[] {
  const readings = [name.trim()];
  let rest = name.trim();
  for (let i = 0; i < MUSIC_MAX_COURTESIES; i++) {
    const courtesy = COURTESIES.find(phrase => rest.endsWith(` ${phrase}`));
    if (!courtesy) break;
    rest = rest.slice(0, -courtesy.length).trim();
    readings.push(rest);
  }
  return readings;
}
